import React, { useMemo } from 'react';
import {
  Box, List, ListItem, Typography, useTheme,
} from '@mui/material';
import {
  blueGrey, common, grey, lightBlue,
} from '@mui/material/colors';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import QuestionMarkIcon from '@mui/icons-material/QuestionMark';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import Decimal from 'decimal.js';
import { ReactComponent as SendMoneyIcon } from '../assets/images/send_money.svg';
import { ReactComponent as PaypalLogo } from '../assets/images/paypal_logo.svg';
import { ReactComponent as BankTransferLogo } from '../assets/images/bank_transfer_logo.svg';
import { WalletPayout, WalletReward } from './constants';
import { useAppSelector } from '../store/hooks';
import { selectCompanyPayoutsWallet, selectCompanyRewardsWallet } from '../store/slices/dashboardSlice';
import { IconBox } from './IconBox';
import { ListViewButton } from './ListViewButton';
import { PaymentDetailStatusType } from './enums';
import { PaymentMethodType } from './models/PaymentMethod';

interface TransactionsListProps {
  transactionsListId: string;
  maxTransactionsShow? : number;
  onBtnClick?: () => void;
  optionalStyles?: React.CSSProperties;
}

export function TransactionsList({
  transactionsListId,
  maxTransactionsShow,
  onBtnClick,
  optionalStyles = {},
}: TransactionsListProps) {
  const theme = useTheme();
  const { t } = useTranslation();
  const companyRewardsWallet = useAppSelector(selectCompanyRewardsWallet);
  const companyPayoutsWallet = useAppSelector(selectCompanyPayoutsWallet);
  const companyTransactions = useMemo(
    () => [
      ...(companyRewardsWallet?.transactions ?? []),
      ...(companyPayoutsWallet?.transactions ?? []),
    ].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()),
    [companyRewardsWallet?.transactions, companyPayoutsWallet?.transactions],
  );

  const TransactionIcon = (
    { transactionType, transactionStatus, payoutMethod }: {
      transactionType: string,
      transactionStatus: string,
      payoutMethod?: string,
    },
  ) => {
    switch (transactionType) {
      case WalletPayout:
        if (
          transactionStatus === PaymentDetailStatusType.PAYMENT_FAILED ||
          transactionStatus === PaymentDetailStatusType.PAYMENT_RETURNED
        ) {
          return (
            <IconBox optionalStyles={{ background: theme.palette.error.main }}>
              <SendMoneyIcon />
            </IconBox>
          );
        }

        if (transactionStatus === PaymentDetailStatusType.PAYMENT_UNCLAIMED) {
          return (
            <IconBox optionalStyles={{ background: blueGrey[200] }}>
              <SendMoneyIcon />
            </IconBox>
          );
        }

        if (payoutMethod === PaymentMethodType.PAYPAL) {
          return (
            <IconBox optionalStyles={{ background: lightBlue[100] }}>
              <PaypalLogo />
            </IconBox>
          );
        }

        if (payoutMethod === PaymentMethodType.BANK_ACCOUNT) {
          return (
            <IconBox optionalStyles={{ background: lightBlue[100] }}>
              <BankTransferLogo />
            </IconBox>
          );
        }

        return (
          <IconBox optionalStyles={{ background: theme.palette.brand.primary }}>
            <SendMoneyIcon/>
          </IconBox>
        );
      case WalletReward:
        return (
          <IconBox optionalStyles={{ background: theme.palette.brand.primary }}>
            <AttachMoneyIcon sx={{ color: common.white, fontSize: 24 }}/>
          </IconBox>
        );
      default:
        return (
          <IconBox optionalStyles={{ background: common.black }}>
            <QuestionMarkIcon sx={{ color: common.white, fontSize: 24 }}/>
          </IconBox>
        );
    }
  };

  const WalletTransactionsList = () => (
    <>
      {companyTransactions
        .slice(0, maxTransactionsShow ?? companyTransactions.length)
        .map((item, index) => (
          <ListItem
            key={index}
            data-testid={`${transactionsListId}-item-${index}`}
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              paddingLeft: 0,
            }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: theme.spacing(2),
              }}>
              <TransactionIcon
                transactionType={item.type}
                transactionStatus={item.status}
                payoutMethod={item.payoutMethod}
              />
              <Box
                id={`${transactionsListId}-item-partner-box-${index}`}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Typography
                  data-testid={`${transactionsListId}-item-partner-${index}`}
                  variant='h15'
                  sx={{
                    [theme.breakpoints.down('sm')]: {
                      fontSize: theme.spacing(1.75),
                    },
                  }}
                >
                  {
                    (() => {
                      if (item.type === WalletPayout) {
                        if (
                          item.status === PaymentDetailStatusType.PAYMENT_FAILED ||
                        item.status === PaymentDetailStatusType.PAYMENT_RETURNED
                        ) {
                          return t('wallet.rewardsPayoutFailed');
                        }
                        if (item.status === PaymentDetailStatusType.PAYMENT_UNCLAIMED) {
                          return t('wallet.rewardsPayoutUnclaimed');
                        }
                        if (item.payoutMethod === PaymentMethodType.BANK_ACCOUNT) {
                          return t('wallet.rewardsPayoutViaBank');
                        }
                        if (item.payoutMethod === PaymentMethodType.PAYPAL) {
                          return t('wallet.rewardsPayoutViaPayPal');
                        }
                        return t('wallet.rewardsPayout');
                      }
                      return item.partner;
                    })()
                  }
                </Typography>
                {item.createdAt && <Typography
                  data-testid={`${transactionsListId}-item-createdAt-${index}`}
                  variant='body8'
                  color='text.secondary'
                  sx={{
                    [theme.breakpoints.down('sm')]: {
                      fontSize: theme.spacing(1.75),
                    },
                  }}
                >
                  {moment(item.createdAt).format('MMMM D, YYYY')}
                </Typography>}
              </Box>
            </Box>
            {item.amount && <Typography
              data-testid={`${transactionsListId}-item-amount-${index}`}
              variant='h15'
              sx={{
                [theme.breakpoints.down('sm')]: {
                  fontSize: theme.spacing(1.75),
                },
              }}
            >
              {item.type === WalletReward ? '+' : ''}${new Decimal(item.amount).toFixed(2)}
            </Typography>}
          </ListItem>
        ))}
    </>
  );

  return (
    <Box
      sx={{
        display: 'flex',
        padding: theme.spacing(3.125),
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: theme.spacing(3.125),
        flex: '1 0 0',
        borderRadius: theme.spacing(1.5),
        border: `1px solid ${grey[200]}`,
        [theme.breakpoints.down('sm')]: { padding: `${theme.spacing(1.875)} ${theme.spacing(1)}` },
        ...optionalStyles,
      }}
    >
      <Typography
        id={'wallet-list-title'}
        variant='h2'
        sx={{
          lineHeight: theme.spacing(3),
          [theme.breakpoints.down('sm')]: {
            fontSize: theme.spacing(1.75),
          },
        }}
      >
        {t('wallet.earningAndRewards')}
      </Typography>
      <List
        sx={{
          width: '100%',
        }}
      >
        <WalletTransactionsList/>
      </List>
      {onBtnClick && <ListViewButton
        onBtnClick={onBtnClick}
        listViewId={transactionsListId}
        btnLabel={t('wallet.viewAllRewards')}
      />}
    </Box>
  );
}
